import { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { useMatch } from 'react-router-dom';

import { GeoLocationStatus } from '@endeavour/enhanced-geo-integration';

import { config } from '../../../../config/config';
import { useGeoLocationHeartbeat } from '../../../geolocation/hooks/use-geolocation-heartbeat';
import { useGeolocationStatus } from '../../../geolocation/util/geolocation-context';
import { getClient } from '../../../main/graphql';
import { CompliancePrompt } from '../../../sessions/components/compliance-prompt/compliance-prompt';
import { BlockType, useCompliancePrompt } from '../../../sessions/hooks/use-compliance-prompt';
import { Loader } from '../../../shared/components/loader/loader';
import { useCurrentCurrency } from '../../../shared/hooks/use-current-currency';
import type { FCWithChildren } from '../../../shared/utils/component.interface';
import type {
    ICreateCashierSessionMutation,
    ICreateCashierSessionMutationVariables,
} from '../../queries/cashier.graphql';
import { CreateCashierSessionDocument, IMethodType } from '../../queries/cashier.graphql';
import { useProviderIntegration } from '../../util/provider-context';

export const CashierContext: FCWithChildren<unknown> = ({ children }) => {
    /**
     * Initialize the cashier integration before allowing access to the cashier.
     */
    const auth = useAuth();
    const userProfileId = auth.user?.profile.user_profile_id as string;
    const isAuthenticated = auth.isAuthenticated;

    const { status, errors } = useGeolocationStatus();
    const { sendHeartbeat } = useGeoLocationHeartbeat();

    const urlMatch = useMatch(':locale/cashier/:methodType/*');
    const methodType =
        urlMatch?.params.methodType === 'deposit' ? IMethodType.Deposit : IMethodType.Withdrawal;
    const currencyCode = useCurrentCurrency();
    const integration = useProviderIntegration();
    const [isReady, setIsReady] = useState(false);
    const { prompt } = useCompliancePrompt(
        methodType === IMethodType.Deposit ? BlockType.DEPOSIT : BlockType.WITHDRAWAL,
    );
    const isGeolocationRequired =
        methodType === IMethodType.Deposit
            ? config.GEOLOCATION.ENHANCED_GEOLOCATION_REQUIRED.DEPOSITS
            : config.GEOLOCATION.ENHANCED_GEOLOCATION_REQUIRED.WITHDRAWALS;

    const requiresStatusCheckFirst = status !== GeoLocationStatus.ALLOWED && isGeolocationRequired;
    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        if (requiresStatusCheckFirst) {
            return;
        }

        let isCancelled = false;
        const client = getClient();
        integration
            .initialize(async (integrationId: string) => {
                const { data } = await client
                    .mutation<
                        ICreateCashierSessionMutation,
                        ICreateCashierSessionMutationVariables
                    >(
                        CreateCashierSessionDocument,
                        {
                            xTenantID: config.TENANT_ID,
                            userProfileId,
                            provider: integrationId,
                            currencyCode,
                            methodType,
                            successUrl: window.location.origin,
                            errorUrl: window.location.origin,
                            backUrl: window.location.origin,
                        },
                        {
                            requestPolicy: 'network-only',
                        },
                    )
                    .toPromise();

                if (!data) {
                    throw new Error('Failed to load session for ' + integrationId);
                }

                const sessionData = data.cashierSessionCreate;

                return {
                    sessionId: sessionData.sessionContextId,
                    providerData: sessionData.providerExtra,
                };
            })
            .then(() => {
                if (isCancelled) {
                    return;
                }

                setIsReady(true);
                sendHeartbeat();
            });

        return () => {
            isCancelled = true;
            integration.destroy();
            setIsReady(false);
        };
    }, [
        integration,
        sendHeartbeat,
        requiresStatusCheckFirst,
        isAuthenticated,
        userProfileId,
        currencyCode,
        methodType,
    ]);

    if (
        status !== GeoLocationStatus.ALLOWED &&
        status !== GeoLocationStatus.PENDING &&
        isGeolocationRequired
    ) {
        return <CompliancePrompt blockType={BlockType.GEOLOCATION} errors={errors} />;
    }

    if (prompt) {
        return prompt;
    }

    if (!isReady) {
        console.log('cashier context isReady', isReady);
        return <Loader />;
    }

    return <>{children}</>;
};
